import { Button, Checkbox, Input, Select } from "components";
import { HeaderWithBack } from "components/HeaderBack";
import { useFormik } from "formik";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getPartnerUuid } from "services/auth";
import {
	createOrUpdateBankAccount,
	getBankAccount
} from "services/bank-account";
import { bankList } from "utils/bankList";
import * as Yup from "yup";
import styles from "./styles.module.scss";

const BankAccountSchema = Yup.object().shape({
	account_type: Yup.string().oneOf(["bank", "pix"]).required(),
	bank_id: Yup.number().notRequired(),
	branch_id: Yup.string()
		.notRequired()
		.matches(/^\d{4}$/, "Campo inválido"),
	account_number: Yup.string()
		.matches(/^\d{6,9}-?\d$/, "Campo inválido")
		.notRequired(),
	pix_key: Yup.string()
		.notRequired()
		.test("pix-key-format", "Chave Pix inválida", (value) => {
			if (!value) return true;
			const cpfCnpj = /^(\d{11}|\d{14})$/;
			const email = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			const telefone = /^\d{10,11}$/;
			const chaveAleatoria =
				/^[a-fA-F0-9]{8}-?[a-fA-F0-9]{4}-?[a-fA-F0-9]{4}-?[a-fA-F0-9]{4}-?[a-fA-F0-9]{12}$/;
			return (
				cpfCnpj.test(value) ||
				email.test(value) ||
				telefone.test(value) ||
				chaveAleatoria.test(value)
			);
		}),
	is_primary: Yup.boolean().required("Campo obrigatório")
});

type UpsertBankAccountFormValues = {
	id: string;
	bank_id: string | undefined;
	branch_id: string | undefined;
	account_number: string | undefined;
	pix_key: string | null;
	is_primary: boolean;
	account_type: "bank" | "pix";
};

export default function UpsertBankAccountPage() {
	const navigate = useNavigate();

	const [partnerId, setPartnerId] = useState("");

	const [submitting, setSubmitting] = useState<boolean>(false);
	const [bankAccount, setBankAccount] = useState<UpsertBankAccountFormValues>({
		id: "",
		bank_id: undefined,
		branch_id: undefined,
		account_number: undefined,
		pix_key: null,
		is_primary: false,
		account_type: "bank"
	});

	const params = useParams();
	const bankAccountId = params.bankAccountId || null;

	const fetchData = async () => {
		const partnerUuid = (await getPartnerUuid()) || "";
		setPartnerId(partnerUuid);

		if (bankAccountId) {
			const apiBankAccount: UpsertBankAccountFormValues =
				(await getBankAccount(partnerUuid, bankAccountId)) || bankAccount;

			setBankAccount({
				...apiBankAccount,
				account_type: apiBankAccount.pix_key ? "pix" : "bank"
			});
		}
	};

	useEffect(() => {
		fetchData();
	}, []);

	const formik = useFormik<UpsertBankAccountFormValues>({
		initialValues: bankAccount,
		validationSchema: BankAccountSchema,
		onSubmit: async (form) => {
			setSubmitting(true);

			const bankAccountResult: any = await createOrUpdateBankAccount(
				partnerId,
				bankAccountId,
				form
			).catch((error) => {
				setSubmitting(false);
				let message = "Algo saiu errado. ";

				if (error?.body.error?.constraint === "bank_account_xor_pix_key") {
					formik.setErrors({
						...formik.errors,
						bank_id: "Preencha a conta bancária ou a chave Pix.",
						branch_id: "Preencha a conta bancária ou a chave Pix.",
						account_number: "Preencha a conta bancária ou a chave Pix.",
						pix_key: "Preencha a conta bancária ou a chave Pix."
					});
					message += "Preencha a conta bancária ou a chave Pix.";
				} else if (error.code < 500) {
					message += "Verifique os dados digitados e tente novamente.";
					if (error.body.bank_id) formik.errors.bank_id = error.body.bank_id;
					if (error.body.branch_id)
						formik.errors.branch_id = error.body.branch_id;
					if (error.body.account_number)
						formik.errors.account_number = error.body.account_number;
					if (error.body.pix_key) formik.errors.pix_key = error.body.pix_key;
					if (error.body.is_primary)
						formik.errors.is_primary = error.body.is_primary;

					if (
						error.body.error?.constraint ===
						"unique_primary_bank_account_per_partner"
					) {
						formik.errors.is_primary =
							"Já existe uma conta bancária principal para este parceiro.";
					}
				} else {
					message += "Entre em contato com o suporte.";
				}

				toast.error(message, {
					position: "bottom-center",
					autoClose: 5000,
					hideProgressBar: true,
					closeOnClick: true,
					pauseOnHover: true,
					draggable: true,
					progress: undefined,
					theme: "light"
				});
			});

			const bank_account_id = bankAccountResult?.id;
			if (!bank_account_id) {
				return;
			}

			navigate("/cadastro/contas");
		},
		enableReinitialize: true
	});

	return (
		<div className={styles.container}>
			<HeaderWithBack label="Nova conta / PIX" backTo={-1} />
			<form className={styles.form} onSubmit={formik.handleSubmit}>
				<div className={styles.upsertContent}>
					<div className={styles.radioGroup}>
						<label htmlFor="account_type_bank">
							<input
								type="radio"
								id="account_type_bank"
								name="account_type"
								value="bank"
								checked={formik.values.account_type === "bank"}
								onChange={() => formik.setFieldValue("account_type", "bank")}
								disabled={!!bankAccountId}
							/>
							&nbsp;&nbsp;Conta Corrente
						</label>
						<label htmlFor="account_type_pix">
							<input
								type="radio"
								id="account_type_pix"
								name="account_type"
								value="pix"
								checked={formik.values.account_type === "pix"}
								onChange={() => formik.setFieldValue("account_type", "pix")}
								disabled={!!bankAccountId}
							/>
							&nbsp;&nbsp;Chave Pix
						</label>
					</div>
					{formik.values.account_type === "bank" && (
						<>
							<Select
								id="bank_id"
								name="bank_id"
								label="Banco"
								placeholder="Escolha um banco"
								value={formik.values.bank_id}
								onChange={(val) => formik.setFieldValue("bank_id", val)}
								error={formik.touched.bank_id && formik.errors.bank_id}
								errorMessage={formik.errors.bank_id}
								options={[
									...bankList.map((bank) => ({
										label: `${bank.value} - ${bank.label}`,
										value: bank.value
									}))
								]}
							/>
							<Input
								id="branch_id"
								name="branch_id"
								placeholder="Digite o número da agência"
								label="Agência"
								value={formik.values.branch_id}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={formik.touched.branch_id && formik.errors.branch_id}
								errorMessage={formik.errors.branch_id}
							/>
							<Input
								id="account_number"
								name="account_number"
								placeholder="Digite o número da conta corrente"
								label="Conta-Corrente"
								value={formik.values.account_number}
								onChange={formik.handleChange}
								onBlur={formik.handleBlur}
								error={
									formik.touched.account_number && formik.errors.account_number
								}
								errorMessage={formik.errors.account_number}
							/>
						</>
					)}

					{formik.values.account_type === "pix" && (
						<Input
							id="pix_key"
							name="pix_key"
							placeholder="Digite sua chave Pix (CPF, CNPJ, e-mail, telefone ou chave aleatória)"
							label="Chave Pix"
							value={formik.values.pix_key || ""}
							onChange={formik.handleChange}
							onBlur={formik.handleBlur}
							error={formik.touched.pix_key && formik.errors.pix_key}
							errorMessage={formik.errors.pix_key}
						/>
					)}

					<Checkbox
						name="is_primary"
						id="is_primary"
						value="1"
						label={<span>Esta é a conta principal</span>}
						onChange={formik.handleChange}
						onBlur={formik.handleBlur}
						checked={formik.values.is_primary}
						error={formik.touched.is_primary && formik.errors.is_primary}
						errorMessage={formik.errors.is_primary}
					/>
				</div>
				<div className={styles.buttonWrapper}>
					<Button type="submit">Salvar</Button>
				</div>
			</form>
		</div>
	);
}
